import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  List,
  ListItem,
  ListIcon,
  useColorMode,
  Progress,
  Avatar,
  AvatarGroup,
  Divider,
} from "@chakra-ui/react";
import {
  CheckIcon,
  ChevronRightIcon,
  TimeIcon,
  StarIcon,
} from "@chakra-ui/icons";
import {
  FaBalanceScale,
  FaVoteYea,
  FaUsers,
  FaHandshake,
  FaRocket,
  FaCrown,
  FaShieldAlt,
  FaChartLine,
  FaLightbulb,
  FaGavel,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../../brandConfig";

const Governance = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 248, 248, 0.9) 50%, rgba(255, 255, 255, 0.95) 100%)`
      : `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder =
    colorMode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)";
  const textPrimary = getColor(
    colorMode === "light" ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === "light" ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const textMuted = getColor(
    colorMode === "light" ? "text.muted" : "text.mutedDark",
    colorMode
  );

  const mockProposals = [
    {
      id: 1,
      title: "Add AI Voice Assistant Module",
      status: "Active",
      votes: { for: 75, against: 15, abstain: 10 },
      endsIn: "2 days",
      proposedBy: "Tom Miller",
      color: "#10B981",
    },
    {
      id: 2,
      title: "Increase Development Fund Allocation to 15%",
      status: "Active",
      votes: { for: 45, against: 35, abstain: 20 },
      endsIn: "5 days",
      proposedBy: "Advisory Council",
      color: "#F59E0B",
    },
    {
      id: 3,
      title: "Launch Web3 Education Course in LMS",
      status: "Passed",
      votes: { for: 92, against: 5, abstain: 3 },
      endsIn: "Ended",
      proposedBy: "Community Member",
      color: "#3B82F6",
    },
  ];

  const governancePhases = [
    {
      phase: "Phase 1: Tom's Benevolent Dictatorship",
      duration: "Months 0-6",
      icon: FaCrown,
      color: "#9333EA",
      features: [
        "Tom makes final decisions on all proposals",
        "Investors provide input via Snapshot polls",
        "Monthly transparency reports",
        "Build governance muscle memory",
      ],
      rationale: "Protect early investors, ensure platform stability, Tom has most skin in the game (40% tokens)",
    },
    {
      phase: "Phase 2: Advisory Council",
      duration: "Months 6-12",
      icon: FaUsers,
      color: "#3B82F6",
      features: [
        "Top 5 token holders form advisory council",
        "Tom listens to council recommendations",
        "Snapshot votes inform decisions",
        "Monthly council meetings",
      ],
      rationale: "More community input while maintaining stability and direction",
    },
    {
      phase: "Phase 3: Progressive Decentralization",
      duration: "Months 12+",
      icon: FaBalanceScale,
      color: "#10B981",
      features: [
        "Snapshot X votes become binding",
        "Tom retains veto power on critical issues",
        "Multi-sig treasury with council",
        "Path to full DAO",
      ],
      rationale: "Community shapes priorities, Tom retains significant influence (40% voting power)",
    },
  ];

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1400px" py={16}>
        <VStack spacing={16} align="stretch">
          {/* Hero */}
          <Box textAlign="center">
            <Badge colorScheme="blue" fontSize="md" px={4} py={2} mb={4}>
              Governance System
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "6xl" }}
              fontWeight="800"
              mb={6}
              color={textPrimary}
              bgGradient="linear(to-r, #3B82F6, #9333EA)"
              bgClip="text"
            >
              Transparent Governance
            </Heading>
            <Text fontSize="xl" color={textSecondary} maxW="800px" mx="auto">
              Your voice matters. Vote on feature priorities, budget allocations,
              and platform direction through decentralized governance powered by
              Snapshot.
            </Text>
          </Box>

          {/* Voting Power */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaVoteYea} w={7} h={7} color="#3B82F6" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  How Voting Works
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                <Box
                  p={6}
                  bg="rgba(59, 130, 246, 0.1)"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="rgba(59, 130, 246, 0.3)"
                  textAlign="center"
                >
                  <Icon as={FaBalanceScale} w={12} h={12} color="#3B82F6" mb={4} />
                  <Heading size="md" mb={3} color={textPrimary}>
                    1 Token = 1 Vote
                  </Heading>
                  <Text fontSize="md" color={textSecondary}>
                    Your voting power is directly proportional to your BB token
                    holdings. More tokens = more influence.
                  </Text>
                </Box>
                <Box
                  p={6}
                  bg="rgba(147, 51, 234, 0.1)"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="rgba(147, 51, 234, 0.3)"
                  textAlign="center"
                >
                  <Icon as={FaShieldAlt} w={12} h={12} color="#9333EA" mb={4} />
                  <Heading size="md" mb={3} color={textPrimary}>
                    Snapshot Voting
                  </Heading>
                  <Text fontSize="md" color={textSecondary}>
                    Free off-chain voting (no gas fees). Token balance at
                    proposal creation = your voting power.
                  </Text>
                </Box>
                <Box
                  p={6}
                  bg="rgba(34, 197, 94, 0.1)"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="rgba(34, 197, 94, 0.3)"
                  textAlign="center"
                >
                  <Icon as={FaHandshake} w={12} h={12} color="#10B981" mb={4} />
                  <Heading size="md" mb={3} color={textPrimary}>
                    Delegation Option
                  </Heading>
                  <Text fontSize="md" color={textSecondary}>
                    Can't vote actively? Delegate your voting power to trusted
                    advisors who vote on your behalf.
                  </Text>
                </Box>
              </SimpleGrid>

              <Divider my={8} />

              <Heading size="md" mb={4} color={textPrimary}>
                Proposal Types
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {[
                  {
                    type: "Feature Requests",
                    threshold: "1,000+ BB",
                    duration: "3 days",
                    majority: ">50%",
                    binding: "Advisory",
                    color: "#3B82F6",
                    example: "Add AI voice assistant module",
                  },
                  {
                    type: "Budget Allocations",
                    threshold: "5,000+ BB",
                    duration: "7 days",
                    majority: "60% supermajority",
                    binding: "Snapshot X (on-chain)",
                    color: "#F59E0B",
                    example: "Allocate $50K for marketing campaign",
                  },
                  {
                    type: "Major Changes",
                    threshold: "10,000+ BB",
                    duration: "14 days",
                    majority: "75% supermajority",
                    binding: "Snapshot X (on-chain)",
                    color: "#EF4444",
                    example: "Change token distribution model",
                  },
                  {
                    type: "Quick Polls",
                    threshold: "100+ BB",
                    duration: "24 hours",
                    majority: "Simple majority",
                    binding: "Non-binding",
                    color: "#10B981",
                    example: "Which feature should we prioritize?",
                  },
                ].map((item, index) => (
                  <Box
                    key={index}
                    p={5}
                    bg={`${item.color}20`}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={`${item.color}40`}
                  >
                    <Badge bg={item.color} color="white" mb={3} px={3} py={1}>
                      {item.type}
                    </Badge>
                    <List spacing={2} fontSize="sm">
                      <ListItem color={textSecondary}>
                        <strong>Threshold:</strong> {item.threshold}
                      </ListItem>
                      <ListItem color={textSecondary}>
                        <strong>Duration:</strong> {item.duration}
                      </ListItem>
                      <ListItem color={textSecondary}>
                        <strong>Majority:</strong> {item.majority}
                      </ListItem>
                      <ListItem color={textSecondary}>
                        <strong>Binding:</strong> {item.binding}
                      </ListItem>
                    </List>
                    <Text fontSize="xs" fontStyle="italic" color={textMuted} mt={3}>
                      Example: "{item.example}"
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Active Proposals (Mock) */}
          <Box>
            <Heading size="xl" mb={4} textAlign="center" color={textPrimary}>
              Active Proposals
            </Heading>
            <Text fontSize="lg" mb={8} textAlign="center" color={textSecondary} maxW="800px" mx="auto">
              View and vote on current proposals. Connect your wallet to participate.
            </Text>
            <VStack spacing={6}>
              {mockProposals.map((proposal) => (
                <Card
                  key={proposal.id}
                  w="100%"
                  bg={cardGradientBg}
                  borderWidth="1px"
                  borderColor={cardBorder}
                  boxShadow="0 4px 16px rgba(0, 0, 0, 0.2)"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                  }}
                  transition="all 0.3s"
                >
                  <CardBody p={6}>
                    <Flex justify="space-between" align="start" mb={4}>
                      <Box flex={1}>
                        <Flex align="center" gap={3} mb={2}>
                          <Heading size="md" color={textPrimary}>
                            {proposal.title}
                          </Heading>
                          <Badge
                            colorScheme={
                              proposal.status === "Active"
                                ? "green"
                                : proposal.status === "Passed"
                                ? "blue"
                                : "gray"
                            }
                          >
                            {proposal.status}
                          </Badge>
                        </Flex>
                        <Text fontSize="sm" color={textMuted}>
                          Proposed by {proposal.proposedBy}
                        </Text>
                      </Box>
                      <Box textAlign="right">
                        <Icon as={TimeIcon} mr={1} color={textMuted} />
                        <Text fontSize="sm" color={textMuted}>
                          {proposal.endsIn}
                        </Text>
                      </Box>
                    </Flex>

                    <Box mb={4}>
                      <Flex justify="space-between" mb={2}>
                        <Text fontSize="sm" color={textSecondary}>
                          For: {proposal.votes.for}%
                        </Text>
                        <Text fontSize="sm" color={textSecondary}>
                          Against: {proposal.votes.against}%
                        </Text>
                      </Flex>
                      <Progress
                        value={proposal.votes.for}
                        size="lg"
                        colorScheme="green"
                        borderRadius="full"
                      />
                      <Text fontSize="xs" color={textMuted} mt={1}>
                        {proposal.votes.abstain}% abstained
                      </Text>
                    </Box>

                    {proposal.status === "Active" && (
                      <Flex gap={3}>
                        <Button
                          flex={1}
                          colorScheme="green"
                          leftIcon={<CheckIcon />}
                        >
                          Vote For
                        </Button>
                        <Button flex={1} colorScheme="red">
                          Vote Against
                        </Button>
                        <Button flex={1} variant="outline">
                          Abstain
                        </Button>
                      </Flex>
                    )}
                    {proposal.status === "Passed" && (
                      <Badge colorScheme="blue" fontSize="md" p={2} w="100%" textAlign="center">
                        ✅ Proposal Passed - Implementation in progress
                      </Badge>
                    )}
                  </CardBody>
                </Card>
              ))}
            </VStack>

            <Flex justify="center" mt={6}>
              <Button
                size="lg"
                bg="#3B82F6"
                color="white"
                rightIcon={<ChevronRightIcon />}
                _hover={{
                  bg: "#2563EB",
                }}
              >
                Connect Wallet to Vote
              </Button>
            </Flex>
          </Box>

          {/* Governance Phases */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaRocket} w={7} h={7} color="#9333EA" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  Progressive Decentralization Roadmap
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <Text fontSize="lg" mb={8} color={textSecondary} textAlign="center">
                Business Builder governance evolves over time, gradually shifting
                power from founder to community.
              </Text>
              <VStack spacing={8} align="stretch">
                {governancePhases.map((phase, index) => (
                  <Box
                    key={index}
                    p={6}
                    bg={`${phase.color}20`}
                    borderRadius="xl"
                    borderWidth="2px"
                    borderColor={`${phase.color}40`}
                    position="relative"
                  >
                    <Badge
                      position="absolute"
                      top="-12px"
                      left="20px"
                      bg={phase.color}
                      color="white"
                      px={4}
                      py={1}
                      fontSize="sm"
                    >
                      {phase.duration}
                    </Badge>
                    <Flex gap={6} align="start">
                      <Icon
                        as={phase.icon}
                        w={12}
                        h={12}
                        color={phase.color}
                        flexShrink={0}
                      />
                      <Box flex={1}>
                        <Heading size="md" mb={3} color={textPrimary}>
                          {phase.phase}
                        </Heading>
                        <List spacing={2} mb={4}>
                          {phase.features.map((feature, i) => (
                            <ListItem key={i} fontSize="sm" color={textSecondary}>
                              <ListIcon as={CheckIcon} color={phase.color} />
                              {feature}
                            </ListItem>
                          ))}
                        </List>
                        <Box
                          p={3}
                          bg="rgba(0, 0, 0, 0.1)"
                          borderRadius="md"
                          fontSize="sm"
                          fontStyle="italic"
                          color={textMuted}
                        >
                          <strong>Why:</strong> {phase.rationale}
                        </Box>
                      </Box>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </CardBody>
          </Card>

          {/* Best Practices */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            <Card
              bg="rgba(34, 197, 94, 0.1)"
              borderWidth="1px"
              borderColor="rgba(34, 197, 94, 0.3)"
            >
              <CardHeader>
                <Heading size="md" color={textPrimary}>
                  <Icon as={FaLightbulb} mr={3} color="#10B981" />
                  For Token Holders
                </Heading>
              </CardHeader>
              <CardBody>
                <List spacing={3}>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#10B981" />
                    Do your research before voting
                  </ListItem>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#10B981" />
                    Ask questions in governance forum
                  </ListItem>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#10B981" />
                    Delegate wisely if you can't participate
                  </ListItem>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#10B981" />
                    Hold Tom accountable with questions
                  </ListItem>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#10B981" />
                    Contribute ideas, not just criticism
                  </ListItem>
                </List>
              </CardBody>
            </Card>

            <Card
              bg="rgba(59, 130, 246, 0.1)"
              borderWidth="1px"
              borderColor="rgba(59, 130, 246, 0.3)"
            >
              <CardHeader>
                <Heading size="md" color={textPrimary}>
                  <Icon as={FaGavel} mr={3} color="#3B82F6" />
                  For Tom (Founder)
                </Heading>
              </CardHeader>
              <CardBody>
                <List spacing={3}>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#3B82F6" />
                    Monthly transparency reports
                  </ListItem>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#3B82F6" />
                    Explain all decisions clearly
                  </ListItem>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#3B82F6" />
                    Respond to proposals within 48 hours
                  </ListItem>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#3B82F6" />
                    Host quarterly town halls
                  </ListItem>
                  <ListItem color={textSecondary}>
                    <ListIcon as={CheckIcon} color="#3B82F6" />
                    Share development roadmap openly
                  </ListItem>
                </List>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* CTA */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #3B82F6 0%, #9333EA 100%)"
            p={12}
            borderRadius="2xl"
            color="white"
          >
            <Icon as={FaVoteYea} w={12} h={12} mb={4} />
            <Heading size="2xl" mb={4}>
              Your Voice Matters
            </Heading>
            <Text fontSize="xl" mb={8} maxW="700px" mx="auto">
              Buy BB tokens, participate in governance, and shape the future of
              Business Builder together.
            </Text>
            <Flex justify="center" gap={4} flexWrap="wrap">
              <Button
                size="lg"
                bg="white"
                color="#3B82F6"
                rightIcon={<ChevronRightIcon />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(255, 255, 255, 0.3)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/businessbuilder/tokenomics")}
              >
                View Token Economics
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderWidth="2px"
                borderColor="white"
                color="white"
                _hover={{
                  bg: "rgba(255, 255, 255, 0.1)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/businessbuilder/treasury")}
              >
                View Treasury
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Governance;
